// src/features/user-portfolios/components/AddToPortfolioModal.tsx
//
// Global "Add to Portfolio" overlay. Mounted ONCE at the app root (App.tsx)
// and driven entirely by the Zustand store, so it costs nothing while closed
// and does not re-render the info page underneath when it opens.
//
// Adapted to this codebase:
//   - Uses the shared `apiClient` (axios) instead of `fetch`, so the WP JWT
//     and the `/api/v1` prefix are attached automatically (a raw fetch would
//     hit the SPA origin unauthenticated and 401).
//   - Uses the shared <Button> primitive + the app's slate/dark theme classes.
//   - Subscribes to individual store slices so only the modal re-renders.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../shared/ui';
import { resolveCurrency } from '../../../shared/utils/currency';
import {
    addTransaction,
    editPortfolioPosition,
    fetchPortfolios,
    type Portfolio,
} from '../api';
import { usePortfolioStore } from '../store/usePortfolioStore';

const todayIso = () => new Date().toISOString().split('T')[0];

export const AddToPortfolioModal = () => {
    // Individual-slice subscriptions: this component re-renders only when the
    // slice it reads changes (Zustand's core performance win).
    const isModalOpen = usePortfolioStore((s) => s.isModalOpen);
    const modalData = usePortfolioStore((s) => s.modalData);
    const modalMode = usePortfolioStore((s) => s.modalMode);
    const closeModal = usePortfolioStore((s) => s.closeModal);

    const navigate = useNavigate();

    // Form state.
    const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
    const [portfoliosLoading, setPortfoliosLoading] = useState(false);
    const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
    const [transactionType, setTransactionType] = useState<'BUY' | 'SELL'>('BUY');
    const [shares, setShares] = useState<number>(1);
    const [brokerFees, setBrokerFees] = useState<number>(0);
    const [pricePerShare, setPricePerShare] = useState<number>(0);
    // The trade's native currency (ANY 3-letter ISO code). Auto-resolved from
    // the listing venue / symbol and overridable via the input below. The
    // backend's Any-to-Any USD hub converts it to the portfolio base currency.
    const [currency, setCurrency] = useState<string>('USD');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Sync the form each time the modal opens. SELL / EDIT are opened from an
    // existing ledger row, so pre-fill the current share count; BUY (the
    // info-page flow) always starts fresh at 1 share.
    useEffect(() => {
        if (modalData) {
            setPricePerShare(modalData.currentPrice);
            if (modalMode === 'SELL' || modalMode === 'EDIT') {
                setShares(modalData.currentQuantity ?? 1);
            } else {
                setShares(1);
            }
            setBrokerFees(0);
            setTransactionType('BUY');
            // Pre-select the asset's TRUE native listing currency dynamically
            // (Info-page resolved currency -> venue -> symbol suffix). If the
            // resolver returns CAD / GBP / MXN it is used verbatim; the user can
            // still override. The backend converts to the portfolio base at read.
            setCurrency(
                resolveCurrency({
                    explicitCurrency: modalData.currency,
                    exchange: modalData.exchange,
                    symbol: modalData.symbol,
                }) || 'USD',
            );
            setError(null);
            setSuccess(null);
        }
    }, [modalData, modalMode]);

    // Fetch the user's portfolios for the dropdown whenever the modal opens.
    useEffect(() => {
        if (!isModalOpen) return;
        let cancelled = false;
        setPortfoliosLoading(true);
        fetchPortfolios()
            .then((data) => {
                if (cancelled) return;
                setPortfolios(data);
                if (data.length > 0) setSelectedPortfolioId(String(data[0].id));
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to fetch portfolios', err);
                setError('Could not load your portfolios. Please try again.');
            })
            .finally(() => {
                if (!cancelled) setPortfoliosLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isModalOpen]);

    // Close on Escape for keyboard users.
    useEffect(() => {
        if (!isModalOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isModalOpen, closeModal]);

    if (!isModalOpen || !modalData) return null;

    // Adapt the modal to the asset class:
    //  - crypto swaps "Shares" -> "Amount (Coins)" and allows 8-decimal fractions;
    //  - the running total lets the user verify their dollar exposure without
    //    doing fractional math in their head.
    const isCrypto = modalData.type === 'CRYPTO';
    const isSell = modalMode === 'SELL';
    const isEdit = modalMode === 'EDIT';
    // EDIT always writes a fresh BUY baseline; SELL forces SELL; the default
    // BUY (info-page) flow keeps the user's manual Buy/Sell toggle choice.
    const effectiveType: 'BUY' | 'SELL' = isEdit
        ? 'BUY'
        : isSell
          ? 'SELL'
          : transactionType;
    const totalCost =
        Number(shares || 0) * Number(pricePerShare || 0) + Number(brokerFees || 0);

    // SMART MATH: Auto-divides/multiplies the Price and Fees by 100 when
    // flipping between Pence (GBX) and Pounds (GBP), so the mathematical value
    // stays correct even though the label changes. Each result is passed
    // through Number(x.toFixed(6)) to scrub binary floating-point drift
    // (e.g. 46.56 / 100 = 0.46559999… -> 0.4656) while preserving the six
    // decimals a fractional GBP price legitimately needs.
    const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCcy = e.target.value.toUpperCase();

        if (currency === 'GBX' && newCcy === 'GBP') {
            setPricePerShare((prev) => Number((prev / 100).toFixed(6)));
            setBrokerFees((prev) => Number((prev / 100).toFixed(6)));
        } else if (currency === 'GBP' && newCcy === 'GBX') {
            setPricePerShare((prev) => Number((prev * 100).toFixed(6)));
            setBrokerFees((prev) => Number((prev * 100).toFixed(6)));
        }

        setCurrency(newCcy);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedPortfolioId) {
            setError('Please select a portfolio.');
            return;
        }

        // Bug 4 FIX: Client-side validation guardrails
        const numShares = Number(shares);
        const numPrice = Number(pricePerShare);

        if (numShares <= 0) {
            setError('Amount/Shares must be greater than 0.');
            return;
        }

        if (numPrice <= 0) {
            setError('Price must be greater than 0.');
            return;
        }

        // Block overselling immediately without waiting for the backend
        if (
            isSell &&
            modalData.currentQuantity !== undefined &&
            numShares > modalData.currentQuantity
        ) {
            setError(
                `Cannot sell ${numShares}. You only hold ${modalData.currentQuantity} ${modalData.symbol}.`,
            );
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = {
                symbol: modalData.symbol,
                instrument_name: modalData.name,
                instrument_type: modalData.type,
                sector: modalData.sector ?? null,
                transaction_type: effectiveType,
                add_date: todayIso(),
                shares: numShares,
                price_per_share: numPrice,
                broker_fees: Number(brokerFees),
                currency,
            } as const;

            if (isEdit) {
                await editPortfolioPosition(
                    Number(selectedPortfolioId),
                    modalData.symbol,
                    payload,
                );
                setSuccess(`Reset ${modalData.symbol} position. Ledger overwritten.`);
            } else {
                await addTransaction(selectedPortfolioId, payload);
                setSuccess(
                    `${effectiveType === 'BUY' ? 'Bought' : 'Sold'} ${shares} ${modalData.symbol}. Transaction saved.`,
                );
            }
            // Tell the dashboard to re-run the Numba engine without a hard reload.
            window.dispatchEvent(new Event('portfolio-updated'));
            // For the info-page "Add Transaction" flow (not the dashboard-originated
            // SELL / EDIT actions), redirect the user to the portfolio they just added
            // to, so they land on its dashboard and immediately see the new position.
            const shouldRedirect = !isSell && !isEdit;
            const targetPortfolioId = selectedPortfolioId;
            // Give the user a moment to see the confirmation, then close (+ redirect).
            setTimeout(() => {
                closeModal();
                if (shouldRedirect) {
                    navigate(`/dashboard/my-portfolios/${targetPortfolioId}`);
                }
            }, 900);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            // Bug 4 FIX: Safely extract the backend's detailed validation/oversell message
            const backendMsg = (err as any).response?.data?.detail;
            setError(
                typeof backendMsg === 'string'
                    ? backendMsg
                    : 'Failed to save transaction. Please try again.',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClasses =
        'w-full rounded-lg border border-slate-300 dark:border-slate-600 ' +
        'bg-white dark:bg-slate-900 text-slate-900 dark:text-white ' +
        'px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400';

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onMouseDown={(e) => {
                // Backdrop click closes; clicks inside the card do not.
                if (e.target === e.currentTarget) closeModal();
            }}
        >
            <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-2xl text-slate-900 dark:text-white">
                <h2
                    className={`text-xl font-bold mb-1 ${
                        isSell
                            ? 'text-amber-500'
                            : isEdit
                              ? 'text-indigo-500 dark:text-indigo-400'
                              : 'text-slate-900 dark:text-white'
                    }`}
                >
                    {isSell
                        ? 'Sell Asset'
                        : isEdit
                          ? 'Edit (Reset Position)'
                          : 'Add to Portfolio'}
                </h2>
                <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
                    {modalData.name}{' '}
                    <span className="font-mono">({modalData.symbol})</span>
                </p>

                {error && (
                    <div className="mb-4 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-400">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="mb-4 rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
                        {success}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Select Portfolio
                        </label>
                        {portfoliosLoading ? (
                            <div className="text-sm text-slate-500 dark:text-slate-400 py-2">
                                Loading your portfolios…
                            </div>
                        ) : portfolios.length === 0 ? (
                            <div className="text-sm text-amber-600 dark:text-amber-400 py-2">
                                You have no portfolios yet. Create one on the
                                “My Portfolios” page first.
                            </div>
                        ) : (
                            <select
                                value={selectedPortfolioId}
                                onChange={(e) => setSelectedPortfolioId(e.target.value)}
                                className={inputClasses}
                                required
                            >
                                {portfolios.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* The manual Buy/Sell toggle is only for the info-page BUY
                        flow. SELL / EDIT modes force the direction, so the toggle
                        is hidden to avoid an ambiguous action. */}
                    {!isSell && !isEdit && (
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Transaction Type
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {(['BUY', 'SELL'] as const).map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setTransactionType(t)}
                                        className={`rounded-lg px-3 py-2 text-sm font-bold border transition ${
                                            transactionType === t
                                                ? t === 'BUY'
                                                    ? 'bg-emerald-600 border-emerald-600 text-white'
                                                    : 'bg-red-600 border-red-600 text-white'
                                                : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Native currency of THIS trade. Immutable on the ledger;
                        every base-currency figure is derived from it via
                        point-in-time FX conversion on the backend. */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Trade Currency (Native ISO)
                        </label>
                        <select
                            value={currency}
                            onChange={handleCurrencyChange}
                            className={`${inputClasses} font-mono font-bold text-blue-600 dark:text-blue-400 cursor-pointer`}
                            required
                        >
                            <option value="USD">$ USD</option>
                            <option value="EUR">€ EUR</option>
                            <option value="GBP">£ GBP (Pounds)</option>
                            <option value="GBX">GBX (Pence)</option>
                            <option value="CAD">CA$ CAD</option>
                            {/* Gracefully surface any exotic resolved code too. */}
                            {currency &&
                                !['USD', 'EUR', 'GBP', 'GBX', 'CAD'].includes(
                                    currency,
                                ) && (
                                    <option value={currency}>{currency}</option>
                                )}
                        </select>
                        <p className="mt-1 text-[10px] text-slate-500">
                            Switching between GBP and GBX automatically scales
                            your price by 100x. The backend converts to your
                            portfolio's base currency at read-time.
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">
                            {isCrypto ? 'Price per Coin' : 'Price per Share'}
                        </label>
                        <input
                            type="number"
                            step="any"
                            // Bug 4 FIX: enforce > 0 minimum natively
                            min={isCrypto ? '0.00000001' : '0.0001'}
                            value={pricePerShare}
                            onChange={(e) => setPricePerShare(Number(e.target.value))}
                            className={inputClasses}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">
                            {isCrypto ? 'Amount (Coins)' : 'Number of Shares'}
                        </label>
                        {/* We KEEP the state variable named "shares" so the backend
                            JSON payload stays { shares: 0.05 } and the API is intact. */}
                        <input
                            type="number"
                            step={isCrypto ? '0.00000001' : 'any'}
                            // Bug 4 FIX: Min limit + Cap the HTML input at the current quantity if selling
                            min={isCrypto ? '0.00000001' : '0.0001'}
                            max={
                                isSell && modalData.currentQuantity !== undefined
                                    ? modalData.currentQuantity
                                    : undefined
                            }
                            value={shares}
                            onChange={(e) => setShares(Number(e.target.value))}
                            className={`${inputClasses} font-mono`}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">
                            {isCrypto ? 'Network / Exchange Fees' : 'Broker Fees'}
                        </label>
                        <input
                            type="number"
                            step="any"
                            min="0"
                            value={brokerFees}
                            onChange={(e) => setBrokerFees(Number(e.target.value))}
                            className={inputClasses}
                        />
                    </div>

                    {/* Institutional UX helper: auto-calculates the total dollar
                        value so the user doesn't need a calculator for fractions. */}
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-3 py-3 shadow-inner">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            Total Investment Value
                        </span>
                        <span className="font-mono text-sm font-bold text-blue-600 dark:text-blue-400">
                            {(() => {
                                const ccy = (currency || 'USD').toUpperCase();
                                // GBX is not an Intl ISO code -> render "GBX".
                                if (ccy === 'GBX') {
                                    return `GBX ${totalCost.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}`;
                                }
                                try {
                                    return new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: ccy,
                                    }).format(totalCost);
                                } catch {
                                    return `${ccy} ${totalCost.toLocaleString(
                                        undefined,
                                        {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        },
                                    )}`;
                                }
                            })()}
                        </span>
                    </div>

                    <div className="flex justify-end gap-3 mt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={closeModal}
                        >
                            Cancel
                        </Button>
                        <button
                            type="submit"
                            disabled={isSubmitting || portfolios.length === 0}
                            className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-bold text-white transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400 disabled:cursor-not-allowed disabled:opacity-50 ${
                                isSell
                                    ? 'bg-amber-600 hover:bg-amber-700'
                                    : isEdit
                                      ? 'bg-indigo-600 hover:bg-indigo-700'
                                      : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {isSubmitting
                                ? 'Processing…'
                                : isSell
                                  ? 'Execute Sell'
                                  : isEdit
                                    ? 'Overwrite Position'
                                    : 'Add Transaction'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddToPortfolioModal;
